import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Check, Plus, ZoomIn } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { services } from '../mock';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Reveal, Stagger, StaggerItem } from '../components/motion/Reveal';
import { Lightbox } from '../components/Lightbox';
import { BeforeAfterSection } from '../components/BeforeAfterSection';
import * as Icons from 'lucide-react';
import { Seo } from '../components/Seo';
import { seoServices } from '../seo';

const ServiceDetail = () => {
  const { slug } = useParams();
  const { t } = useLanguage();
  const service = services.find(s => s.slug === slug);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const IconComponent = Icons[service.icon];
  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const lightboxSlides = (service.gallery || []).map((src) => ({ type: 'image', src }));

  return (
    <div className="min-h-screen bg-[#000000] pt-32 pb-24">
      <Seo title={seoServices[slug].title} description={seoServices[slug].description} image={`https://www.matrixprint.si${service.heroImage || service.image}`} />
      <div className="container mx-auto px-5 md:px-10">
        <Link to="/services">
          <Button className="btn-secondary mb-8">
            <ArrowLeft className="mr-2 w-5 h-5" />
            {t({ sl: 'Nazaj na storitve', en: 'Back to Services', it: 'Torna ai Servizi' })}
          </Button>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <Reveal>
            <div className="w-20 h-20 rounded-full bg-[white] flex items-center justify-center mb-6">
              <IconComponent className="w-10 h-10 text-[#000000]" />
            </div>
            <h1 className="heading-2 text-[white] mb-6">
              {t(service.title)}
            </h1>
            <p className="body-large text-[white] mb-8">
              {t(service.details)}
            </p>
            <Link to="/contact">
              <Button className="btn-primary min-w-[250px]">
                {t({ sl: 'Pridobite svojo ponudbo', en: 'Get Your Quote', it: 'Ottieni il Tuo Preventivo' })}
              </Button>
            </Link>
          </Reveal>
          <Reveal delay={0.15} y={40}>
            {/* Hero image - full width without background */}
            <div className="rounded-lg overflow-hidden h-[400px]">
              <img
                src={service.heroImage || service.image}
                alt={t(service.title)}
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>
        </div>

        {/* Reference Gallery Section */}
        <div className="mb-16">
          <Reveal>
            <h2 className="heading-4 text-[white] mb-8">
              {t({ sl: 'Galerija referenc', en: 'Reference Gallery', it: 'Galleria di Riferimenti' })}
            </h2>
          </Reveal>
          <Stagger className="grid gap-4 grid-cols-2 md:grid-cols-4" stagger={0.08} data-testid="service-gallery">
            {service.gallery && service.gallery.length > 0 ? (
              service.gallery.map((img, idx) => (
                <StaggerItem
                  key={img} 
                  className="aspect-square rounded-lg overflow-hidden cursor-pointer group relative"
                  onClick={() => openLightbox(idx)}
                >
                  <img
                    src={img}
                    alt={`Reference ${idx + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
                      <ZoomIn className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </StaggerItem>
              ))
            ) : (
              // Placeholder boxes for empty gallery
              <>
                {[1, 2, 3, 4].map((num) => (
                  <StaggerItem 
                    key={num} 
                    className="aspect-square rounded-lg bg-white border border-dashed border-[rgba(0,0,0,0.2)] flex items-center justify-center"
                  >
                    <div className="text-center">
                      <Plus className="w-8 h-8 text-[#ccc] mx-auto mb-2" />
                      <span className="text-[#999] text-sm">
                        {t({ sl: 'Referenca', en: 'Reference', it: 'Riferimento' })} {num}
                      </span>
                    </div>
                  </StaggerItem>
                ))}
              </>
            )}
          </Stagger>
        </div>

        <BeforeAfterSection category={service.id} />

        <Reveal y={36}>
        <Card className="bg-[#1a1a1a] border-[rgba(255, 255, 255, 0.1)]">
          <CardContent className="p-8">
            <h2 className="heading-5 text-[white] mb-6">
              {service.slug === 'uv-printing' 
                ? t({ sl: 'Zakaj izbrati UV tisk?', en: 'Why choose UV printing?', it: 'Perché scegliere la stampa UV?' })
                : service.slug === 'large-format'
                ? t({ sl: 'Zakaj izbrati velikoformatni tisk?', en: 'Why choose large format printing?', it: 'Perché scegliere la stampa grande formato?' })
                : service.slug === 'laser-engraving'
                ? t({ sl: 'Zakaj izbrati lasersko graviranje?', en: 'Why choose laser engraving?', it: 'Perché scegliere l\'incisione laser?' })
                : service.slug === 'light-signs'
                ? t({ sl: 'Zakaj izbrati svetlobne table?', en: 'Why choose light boxes?', it: 'Perché scegliere le insegne luminose?' })
                : t({ sl: 'Kaj vključuje ta storitev:', en: 'What this service includes:', it: 'Cosa include questo servizio:' })
              }
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {service.features[t({ sl: 'sl', en: 'en', it: 'it' })].map((feature, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[white] flex items-center justify-center flex-shrink-0 mt-1">
                    <Check className="w-4 h-4 text-[#000000]" />
                  </div>
                  <p className="body-medium text-[white]">{feature}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        </Reveal>

        <Reveal className="mt-16 bg-[#1a1a1a] border border-[rgba(255, 255, 255, 0.1)] p-8 rounded-lg text-center">
          <h3 className="heading-5 text-[white] mb-4">
            {t({ sl: 'Potrebujete pomoč pri odločitvi?', en: 'Need help deciding?', it: 'Avete bisogno di aiuto per decidere?' })}
          </h3>
          <p className="body-medium text-[#b0b0b0] mb-6 max-w-2xl mx-auto">
            {t({
              sl: 'Naša ekipa strokovnjakov vam bo z veseljem svetovala in pomagala izbrati najboljšo rešitev za vaše potrebe.',
              en: 'Our team of experts will be happy to advise you and help you choose the best solution for your needs.',
              it: 'Il nostro team di esperti sarà lieto di consigliarvi e aiutarvi a scegliere la soluzione migliore per le vostre esigenze.'
            })}
          </p>
          <Link to="/contact">
            <Button className="btn-primary min-w-[200px]">
              {t({ sl: 'Kontaktirajte nas', en: 'Contact Us', it: 'Contattaci' })}
            </Button>
          </Link>
        </Reveal>
      </div>

      {lightboxIndex !== null && service.gallery && (
        <Lightbox slides={lightboxSlides} title={t(service.title)} initialIndex={lightboxIndex} onClose={closeLightbox} testId="service-lightbox" />
      )}
    </div>
  );
};

export default ServiceDetail;
